import { useFormContext } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'
import { MileageRateConfigurationFragment } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { fromCents } from 'modules/core/lib/formatCurrencyUtils'

import AmountField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/BasicInformation/AmountField'
import CurrencyField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/BasicInformation/CurrencyField'
import RateField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/mileage/MileageInformation/RateField'
import InlineFields from 'modules/expenses/components/expenses/Form/InlineFields'
import MileageAlertCallout from 'modules/expenses/components/expenses/MileageAlertCallout'
import { convertToNumber } from 'modules/expenses/lib/numberFormating'

import { CustomizationProps } from '../../../withCustomizationFields'
import { PermissionsProps } from '../../../withFieldPermissions'
import MileageField from './MileageField'
import UnitsField from './UnitsField'

interface Props extends PermissionsProps, CustomizationProps {
  rates?: MileageRateConfigurationFragment[]
  isCalculatingDistance?: boolean
  setIsCalculatingDistance?: (loading: boolean) => void
  isFieldReadOnly?: boolean
}

export const MileageInformation = (props: Props) => {
  const { rates, isCalculatingDistance, setIsCalculatingDistance, isFieldReadOnly } = props
  const { watch, setValue, getValues } = useFormContext()
  const currency = watch('currency')
  const units = watch('units')
  const mileage = watch('mileage')
  const rate = watch('rate')
  const calculatedMileage = watch('calculatedMileage')
  const roundTrip = watch('roundTrip')

  const group = getValues('group')

  const calculatedMileageWithRoundtrip = calculatedMileage * 2

  const showMileageChangedAlert = roundTrip
    ? calculatedMileageWithRoundtrip !== mileage
    : calculatedMileage !== mileage

  useUnsafeEffect(() => {
    if (mileage && rate) {
      const mileageValue = mileage ? convertToNumber(mileage) : 0

      const rateValue = rate ? convertToNumber(rate) : 0

      const calculatedAmount = Math.round(mileageValue * rateValue * 100)

      setValue('amount', fromCents(calculatedAmount), { shouldValidate: true, shouldDirty: true })
    }
  }, [mileage, rate, setValue])

  return (
    <Box gap="s24">
      <InlineFields>
        <CurrencyField
          {...props}
          fieldIdentifier="currency"
          isAutomaticExpensable={false}
          isFieldReadOnly={!!group?.id || isFieldReadOnly}
        />
        <RateField {...props} fieldIdentifier="rate" rates={rates} />
      </InlineFields>
      <InlineFields>
        <UnitsField
          {...props}
          fieldIdentifier="units"
          setIsCalculatingDistance={setIsCalculatingDistance}
        />
        <MileageField {...props} fieldIdentifier="mileage" />
      </InlineFields>
      <AmountField {...props} fieldIdentifier="amount" currency={currency} isAutomaticExpensable />
      <Box width="full">
        {showMileageChangedAlert && !!calculatedMileage && !isCalculatingDistance && (
          <MileageAlertCallout
            roundTrip={roundTrip}
            calculatedMileage={Number(calculatedMileage)}
            units={units}
          />
        )}
      </Box>
    </Box>
  )
}
